/**
 * Dialog reutilizável para importar HTML de e-mail.
 *
 * Suporta upload de arquivo .html/.htm OU paste em textarea como fallback.
 * Valida tamanho (1 MB) no client antes de submeter — server faz validação
 * autoritativa (normalização + sanitização + re-hospedagem de imagens).
 *
 * Layout: o dialog tem altura máxima de viewport e usa flex column. O bloco
 * central (drop zone ou textarea) ocupa o espaço disponível com scroll
 * interno — assim um paste grande não empurra o footer pra fora da tela.
 *
 * Quando `hasExistingContent=true`, exibe um aviso de substituição antes
 * de habilitar o botão "Importar".
 */

import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Code2, Upload, FileText, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 1_000_000;

export interface ImportHtmlDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (html: string) => Promise<void> | void;
  /** Se true, mostra o aviso "isto substituirá o conteúdo atual". */
  hasExistingContent?: boolean;
  isSubmitting?: boolean;
}

export function ImportHtmlDialog({
  open,
  onClose,
  onImport,
  hasExistingContent = false,
  isSubmitting = false,
}: ImportHtmlDialogProps) {
  const [mode, setMode] = useState<"file" | "paste">("file");
  const [pasteValue, setPasteValue] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function resetState() {
    setPasteValue("");
    setFileName(null);
    setFileContent(null);
    setConfirmed(false);
    setMode("file");
    setDragOver(false);
  }

  function handleClose() {
    if (isSubmitting) return;
    resetState();
    onClose();
  }

  async function readFile(file: File) {
    if (file.size > MAX_BYTES) {
      toast.error(`Arquivo grande demais (limite ${(MAX_BYTES / 1_000_000).toFixed(1)} MB)`);
      return;
    }
    const name = file.name.toLowerCase();
    if (!name.endsWith(".html") && !name.endsWith(".htm") && file.type !== "text/html") {
      toast.error("Envie um arquivo .html ou .htm");
      return;
    }
    const text = await file.text();
    setFileContent(text);
    setFileName(file.name);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void readFile(f);
    e.target.value = "";
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) void readFile(f);
  }

  const pasteSize = pasteValue.length;
  const canSubmit =
    !isSubmitting &&
    (hasExistingContent ? confirmed : true) &&
    (mode === "file" ? !!fileContent : pasteValue.trim().length > 0);

  async function submit() {
    const html = mode === "file" ? fileContent : pasteValue.trim();
    if (!html) return;
    if (html.length > MAX_BYTES) {
      toast.error("Conteúdo maior que 1 MB.");
      return;
    }
    await onImport(html);
    resetState();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      {/*
        Dialog precisa de altura máxima e flex-column para que o conteúdo do
        meio (textarea / dropzone) seja a única região que cresce; header e
        footer ficam fixos. Override do `grid` padrão do shadcn DialogContent.
      */}
      <DialogContent className="w-[95vw] max-w-2xl mx-2 sm:mx-0 max-h-[85vh] !grid-rows-none flex flex-col gap-3 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Importar HTML</DialogTitle>
          <DialogDescription>
            Envie o HTML <strong>final</strong> do seu e-mail (ex: export do Mailchimp, Stripo, Beefree). Não cole código-fonte de páginas web — e-mails não executam JavaScript, e o destinatário só veria o estado de loading. Imagens são re-hospedadas automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 shrink-0">
          <Button
            type="button"
            variant={mode === "file" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("file")}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Arquivo
          </Button>
          <Button
            type="button"
            variant={mode === "paste" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("paste")}
            className="gap-2"
          >
            <Code2 className="h-4 w-4" />
            Colar código
          </Button>
        </div>

        {/* Região central — única que cresce/encolhe com o viewport. */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {mode === "file" ? (
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".html,.htm,text/html"
                onChange={handleFileSelect}
              />
              {fileContent ? (
                <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/30 p-4 flex items-center gap-3">
                  <FileText className="h-8 w-8 text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{fileName}</p>
                    <p className="text-xs text-muted-foreground">
                      {(fileContent.length / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting}
                  >
                    Trocar
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  disabled={isSubmitting}
                  className={`w-full rounded-lg border-2 border-dashed transition-all p-8 flex flex-col items-center justify-center gap-2 cursor-pointer
                    ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-muted-foreground/30 hover:border-emerald-400 hover:bg-emerald-50/40"}`}
                >
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Arraste um arquivo .html ou clique para selecionar</p>
                  <p className="text-xs text-muted-foreground">Limite 1 MB</p>
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col gap-1">
              {/*
                Textarea nativo (não shadcn) porque o componente padrão usa
                `field-sizing-content`, que cresce com o conteúdo e quebra
                o layout no paste grande. Aqui forçamos h-full + resize-none
                + overflow-auto para que o scroll fique interno.
              */}
              <textarea
                value={pasteValue}
                onChange={(e) => setPasteValue(e.target.value)}
                placeholder="<html>...</html>"
                disabled={isSubmitting}
                spellCheck={false}
                className="flex-1 min-h-[180px] w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs leading-relaxed shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] resize-none overflow-auto disabled:cursor-not-allowed disabled:opacity-50"
              />
              {pasteSize > 0 && (
                <p className="text-[11px] text-muted-foreground shrink-0">
                  {(pasteSize / 1024).toFixed(1)} KB
                  {pasteSize > MAX_BYTES && (
                    <span className="text-destructive ml-2">acima do limite de 1 MB</span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        {hasExistingContent && (
          <label className="shrink-0 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm cursor-pointer">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <span className="flex-1">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={isSubmitting}
                className="mr-2"
              />
              Estou ciente de que isto vai <strong>substituir todo o conteúdo atual</strong> do e-mail.
            </span>
          </label>
        )}

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!canSubmit} className="gap-2">
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {isSubmitting ? "Importando..." : "Importar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
